// Quoted price per slot over the decision window, one line per ticker, drawn to a 0-100¢ scale with the
// 50¢ line for orientation. Decisions arrive newest first; the chart reads left to right in time.

import { useMemo } from 'react';
import type { Decision } from '@/lib/rustLane/api';

const COLORS = ['#4fa3ff', '#ff9f43', '#3ddc97', '#c792ea'];

export function QuoteChart({ decisions }: { decisions: Decision[] }) {
  const series = useMemo(() => {
    const ordered = [...decisions].reverse();
    const byTicker = new Map<string, Array<{ t: number; p: number }>>();
    for (const d of ordered) {
      const t = Number(d.atMs);
      for (const q of d.quotes ?? []) {
        const arr = byTicker.get(q.ticker) ?? [];
        arr.push({ t, p: q.price / 100 });
        byTicker.set(q.ticker, arr);
      }
    }
    const t0 = ordered[0] ? Number(ordered[0].atMs) : 0;
    const t1 = ordered[ordered.length - 1] ? Number(ordered[ordered.length - 1]!.atMs) : 1;
    return { byTicker, t0, t1: Math.max(t1, t0 + 1) };
  }, [decisions]);

  if (series.byTicker.size === 0) return <p className="text-muted-foreground font-mono text-xs">no quotes in the window</p>;
  const W = 800; const H = 160; const padL = 28; const padB = 16;
  const x = (t: number) => padL + ((t - series.t0) / (series.t1 - series.t0)) * (W - padL - 4);
  const y = (p: number) => 4 + (1 - p / 100) * (H - padB - 8);
  const span = series.t1 - series.t0;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Quoted price per ticker over time">
        {[0, 25, 50, 75, 100].map((p) => (
          <g key={p}>
            <line x1={padL} x2={W - 4} y1={y(p)} y2={y(p)} stroke="currentColor" className="text-border" strokeWidth={p === 50 ? 1 : 0.5} strokeDasharray={p === 50 ? '4 3' : undefined} vectorEffect="non-scaling-stroke" />
            <text x={padL - 4} y={y(p) + 3} textAnchor="end" className="fill-muted-foreground font-mono text-[9px]">{p}¢</text>
          </g>
        ))}
        {[...series.byTicker.entries()].map(([ticker, pts], i) => (
          <path key={ticker} d={pts.map((pt, j) => `${j === 0 ? 'M' : 'L'}${x(pt.t).toFixed(1)},${y(pt.p).toFixed(1)}`).join(' ')} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        ))}
        <text x={padL} y={H - 3} className="fill-muted-foreground font-mono text-[9px]">{new Date(series.t0).toISOString().slice(11, 19)}Z</text>
        <text x={W - 4} y={H - 3} textAnchor="end" className="fill-muted-foreground font-mono text-[9px]">{new Date(series.t1).toISOString().slice(11, 19)}Z · {(span / 60000).toFixed(1)} min</text>
      </svg>
      <div className="mt-1 flex flex-wrap gap-3 font-mono text-[11px]">
        {[...series.byTicker.keys()].map((t, i) => (
          <span key={t}><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{t}</span>
        ))}
      </div>
    </div>
  );
}
